export const AttributionTypeConst = {
  ACTUALIZED: 'actualized',
  RAW: 'raw'
} as const;
export type AttributionType = (typeof AttributionTypeConst)[keyof typeof AttributionTypeConst];

export const DateRangePresetConst = {
  T3M: 'T3M',
  T6M: 'T6M',
  T12M: 'T12M',
  YTD: 'YTD',
  ALL: 'ALL',
  CUSTOM: 'CUSTOM'
} as const;
export type DateRangePreset = (typeof DateRangePresetConst)[keyof typeof DateRangePresetConst];

export const HierarchyLevelConst = {
  CHANNEL: 'channel',
  PLATFORM: 'platform',
  TACTIC: 'tactic',
  BRAND_NONBRAND: 'brand_nonbrand'
} as const;
export type HierarchyLevel = (typeof HierarchyLevelConst)[keyof typeof HierarchyLevelConst];

export interface DateRange {
  preset: DateRangePreset;
  startDate: string;
  endDate: string;
  label: string;
}

export interface ModelInfo {
  client: string;
  kpi: string;
  kpiType: 'revenue' | 'non-revenue';
  dateGranularity: string;
  refreshDate: string;
  trainStartDate: string;
  trainEndDate: string;
  testStartDate: string;
  testEndDate: string;
  trainSize: number;
  testSize: number;
  trainRsquared: number;
  testRsquared: number;
  testMape: number;
  testMae: number;
  nObservations: number;
  meanSpend: number;
  medianSpend: number;
  confidenceLevel: number;
}

export interface CPTBRow {
  date: string;
  name: string;
  channel: string;
  platform: string;
  tactic: string;
  brandNonbrand: string;
  incrementalityTest: boolean;
  mediaType: string;
  rawSpend: number;
  effectiveSpend: number;
  effectiveNormalized: number;
  ciLo: number;
  ciHi: number;
}

export interface ActualVsPredictedRow {
  date: string;
  actualKpi: number;
  expectedKpi: number;
  baselinePrediction: number;
  trainTest: string;
}

export interface ROASRow {
  name: string;
  channel: string;
  platform: string;
  tactic: string;
  brandNonbrand: string;
  incrementalityTest: boolean;
  mediaType: string;
  darkLastQuarter: boolean;
  previousQtrRoas: number | null;
  effectivePreviousQtrRoas: number | null;
  previousQtrCpa: number | null;
  effectivePreviousQtrCpa: number | null;
  allTimeRoas: number | null;
  effectiveAllTimeRoas: number | null;
  allTimeCpa: number | null;
  effectiveAllTimeCpa: number | null;
  previousQtrMeanSpend: number;
  effectivePreviousQtrMeanSpend: number;
  responsePreviousQtrMeanSpend: number;
  effectiveResponsePreviousQtrMeanSpend: number;
  nextDollarEffectiveness: number;
  effectiveNextDollarEffectiveness: number;
  spendNeededForOneMoreKpi: number | null;
}

export interface SaturationRow {
  name: string;
  channel: string;
  platform: string;
  tactic: string;
  brandNonbrand: string;
  mediaType: string;
  adstockSpend: number;
  effectiveAdstockSpend: number;
  response: number;
  effectiveResponse: number;
  returnNextDollar: number;
  effectiveReturnNextDollar: number;
  isMean: boolean;
  ciLoResponse: number;
  ciHiResponse: number;
  ciLoReturnNextDollar: number;
  ciHiReturnNextDollar: number;
}

export interface DecayRow {
  period: number;
  name: string;
  spendCarryover: number;
  maxLag: number;
  ciLoCarryover: number;
  ciHiCarryover: number;
}

export interface ChannelInfo {
  name: string;
  channel: string;
  platform: string;
  tactic: string;
  brandNonbrand: string;
  mediaType: string;
  incrementalityTest: boolean;
  isDark: boolean;
}

export interface ChannelHierarchy {
  channels: ChannelInfo[];
  uniqueChannels: string[];
  uniquePlatforms: string[];
  uniqueTactics: string[];
  uniqueBrandNonbrand: string[];
  uniqueMediaTypes: string[];
}

export interface ValidationResult {
  totalBaseline: number;
  totalChannelContrib: number;
  totalActualKpi: number;
  pctOfActual: number;
  passed: boolean;
}

export interface TwoStageDecompRow {
  date: string;
  primaryName: string;
  primaryMediaType: string;
  secondaryName: string;
  secondaryMediaType: string;
  categoryType: 'added' | 'reduced';
  spendOrEffect: 'spend' | 'effect';
  value: number;
}

export interface TwoStageDecompEntry {
  primaryName: string;
  primaryMediaType: string;
  secondaryName: string;
  secondaryMediaType: string;
  categoryType: 'added' | 'reduced';
  // Normalized to observed week by week, like CPTBRow.effectiveNormalized (see
  // aggregateTwoStageDecomp), so it reconciles the direct and actualized totals.
  effect: number;
  spend: number;
}

export type TwoStageDecompMap = Map<string, TwoStageDecompEntry[]>;

export interface AttributedData {
  cptb: CPTBRow[];
  cptbT12M: CPTBRow[];
  roas: ROASRow[];
  saturation: SaturationRow[];
}

// One paid-media feature in one market for one week. `geo` is the model's label: `dma_501`, or
// `other` for every DMA outside the model, pooled into one.
export interface MarketRow {
  geo: string;
  date: string;
  name: string;
  spend: number;
  contribution: number;
  ciLo: number;
  ciHi: number;
}

export interface MarketSalesRow {
  geo: string;
  date: string;
  actualKpi: number;
}

export interface MarketData {
  actualized: MarketRow[];
  raw: MarketRow[];
  sales: MarketSalesRow[];
  // mape_by_geo by geo, already in percent (9.6 is 9.6%).
  accuracy: Record<string, number>;
}

// Where a market sits against a channel's national return: clearly above or below means its
// whole likely range is on that side.
export const MarketStandingConst = {
  ABOVE: 'above',
  BELOW: 'below',
  SAME: 'same'
} as const;
export type MarketStanding = (typeof MarketStandingConst)[keyof typeof MarketStandingConst];

export interface DashboardData {
  modelInfo: ModelInfo;
  actualized: AttributedData;
  raw: AttributedData;
  actualVsPredicted: ActualVsPredictedRow[];
  actualVsPredictedT12M: ActualVsPredictedRow[];
  decay: DecayRow[];
  channelHierarchy: ChannelHierarchy;
  // One color per channel for this run (normalized channel name → hex), so no two channels
  // on the page share a color. Read it through resolveChannelColor.
  channelColors: ReadonlyMap<string, string>;
  twoStageDecomp: TwoStageDecompMap;
  twoStageDecompRows: TwoStageDecompRow[];
  t12mStartDate: string;
  t12mEndDate: string;
  dataStartDate: string;
  dataEndDate: string;
  validation: ValidationResult;
}
